import { speechChunks, SPEECHIFY_MODEL } from "@vladrss/core";

export type SpeechAudio = { kind: "cloud"; blob: Blob } | { kind: "browser"; text: string };
export interface Playing { done: Promise<void>; pause(): void; resume(): void; rate(value: number): void; }
export interface Speaker {
  begin(): void;
  prepare(text: string, signal: AbortSignal): Promise<SpeechAudio>;
  play(source: SpeechAudio, rate: number, signal: AbortSignal): Playing;
  fallback(text: string): SpeechAudio;
  readonly cloud: boolean;
}

const KEY = "vladrss.speechify.tab-key";
const VOICE = "vladrss.speechify.voice";
const RETRY = "vladrss.speechify.retry-after";
const API = "https://api.speechify.ai/v1";
const stored = (store: "sessionStorage" | "localStorage", key: string) => { try { return window[store].getItem(key) ?? ""; } catch { return ""; } };
const save = (store: "sessionStorage" | "localStorage", key: string, value: string) => { try { if (value) window[store].setItem(key, value); else window[store].removeItem(key); } catch { /* memory-only is usable */ } };
let key = stored("sessionStorage", KEY);
let voice = stored("localStorage", VOICE);
let retryAt = Number(stored("localStorage", RETRY)) || 0;
let failed = false;
let keyVersion = 0;
export const voiceSettings = () => ({ hasKey: !!key, voice, keyVersion });
export function setSpeechKey(value: string) { key = value.trim(); keyVersion++; voice = ""; failed = false; retryAt = 0; save("sessionStorage", KEY, key); save("localStorage", VOICE, ""); save("localStorage", RETRY, ""); }
export function setSpeechVoice(value: string) { voice = value; failed = false; save("localStorage", VOICE, value); }
export const listenRate = () => { const n = Number(stored("localStorage", "vladrss.listen.rate")); return n >= 1 && n <= 1.2 ? n : 1; };
export const setListenRate = (n: number) => save("localStorage", "vladrss.listen.rate", String(n));
export async function speechVoices(signal?: AbortSignal): Promise<{ id: string; name: string }[]> {
  if (!key) return [];
  const response = await fetch(`${API}/voices?locale=en&model=${SPEECHIFY_MODEL}&limit=200`, { headers: { Authorization: `Bearer ${key}` }, signal: AbortSignal.any([...(signal ? [signal] : []), AbortSignal.timeout(15_000)]) });
  if (!response.ok) throw new Error(response.status === 401 || response.status === 403 ? "Speechify rejected this key" : "Couldn't load Speechify voices");
  const data = await response.json();
  return (Array.isArray(data.voices) ? data.voices : []).filter((v: { id?: string }) => typeof v.id === "string").map((v: { id: string; display_name?: string }) => ({ id: v.id, name: v.display_name ?? v.id }));
}

/** The key is supplied by the owner in this tab, never bundled or sent to our servers. */
export class BrowserSpeaker implements Speaker {
  constructor(private readonly notice: (text: string) => void) {}
  private announced = false;
  begin() { failed = false; this.announced = false; }
  get cloud() { return !!key && !failed && Date.now() >= retryAt; }
  fallback(text: string): SpeechAudio {
    failed = true;
    if (key && !this.announced) { this.announced = true; this.notice("Using the browser voice for this Listen run"); }
    return { kind: "browser", text };
  }
  async prepare(text: string, signal: AbortSignal): Promise<SpeechAudio> {
    if (!this.cloud) return this.fallback(text);
    const wantedKey = key;
    try {
      if (!voice) {
        const voices = await speechVoices(signal);
        signal.throwIfAborted();
        if (key !== wantedKey) throw new DOMException("Key changed", "AbortError");
        if (!voices.length) throw new Error("No voices");
        setSpeechVoice(voices[0]!.id);
      }
      const safe = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      const r = await fetch(`${API}/audio/speech`, {
        method: "POST", headers: { Authorization: `Bearer ${wantedKey}`, "Content-Type": "application/json" },
        body: JSON.stringify({ input: `<speak>${safe}</speak>`, voice_id: voice, model: SPEECHIFY_MODEL, language: "en-US", audio_format: "mp3" }),
        signal: AbortSignal.any([signal, AbortSignal.timeout(30_000)]),
      });
      if (!r.ok) {
        if (r.status === 402) { retryAt = Date.now() + 3_600_000; save("localStorage", RETRY, String(retryAt)); }
        throw new Error("Synthesis refused");
      }
      const data = await r.json(); signal.throwIfAborted();
      const bytes = Uint8Array.from(atob(data.audio_data), c => c.charCodeAt(0));
      if (!bytes.length) throw new Error("Empty audio");
      return { kind: "cloud", blob: new Blob([bytes], { type: data.audio_format === "wav" ? "audio/wav" : "audio/mpeg" }) };
    } catch (error) { if (signal.aborted || (error as Error).name === "AbortError") throw error; return this.fallback(text); }
  }
  play(source: SpeechAudio, rate: number, signal: AbortSignal): Playing {
    return source.kind === "cloud" ? audioPlayback(source.blob, rate, signal) : browserPlayback(source.text, rate, signal);
  }
}

function audioPlayback(blob: Blob, rate: number, signal: AbortSignal): Playing {
  const url = URL.createObjectURL(blob), audio = new Audio(url);
  audio.playbackRate = rate;
  let reject!: (e: unknown) => void;
  const done = new Promise<void>((resolve, fail) => {
    reject = fail; audio.onended = () => resolve(); audio.onerror = () => fail(new Error("Audio could not play"));
    void audio.play().catch(fail);
  });
  const abort = () => { audio.pause(); reject(signal.reason ?? new DOMException("Stopped", "AbortError")); };
  signal.addEventListener("abort", abort, { once: true });
  if (signal.aborted) abort();
  return { done: done.finally(() => { signal.removeEventListener("abort", abort); audio.onended = null; audio.onerror = null; audio.pause(); audio.removeAttribute("src"); audio.load(); URL.revokeObjectURL(url); }), pause: () => audio.pause(), resume: () => { void audio.play().catch(reject); }, rate: n => { audio.playbackRate = n; } };
}

function browserPlayback(text: string, initialRate: number, signal: AbortSignal): Playing {
  let paused = false, rate = initialRate, current: SpeechSynthesisUtterance | null = null;
  let wake: (() => void) | undefined;
  const abort = () => { if ("speechSynthesis" in window) speechSynthesis.cancel(); wake?.(); };
  const done = (async () => {
    if (!("speechSynthesis" in window)) throw new Error("This browser has no read-aloud voice");
    // cancel() empties the device queue but can leave its global paused flag set.
    if (speechSynthesis.paused) speechSynthesis.resume();
    for (const part of speechChunks(text, 300)) {
      signal.throwIfAborted();
      while (paused) { await new Promise<void>(resolve => { wake = resolve; }); signal.throwIfAborted(); }
      await new Promise<void>((resolve, reject) => {
        current = new SpeechSynthesisUtterance(part); current.rate = rate; current.lang = "en-US";
        const cancelled = () => { speechSynthesis.cancel(); reject(signal.reason ?? new DOMException("Stopped", "AbortError")); };
        const clear = () => signal.removeEventListener("abort", cancelled);
        current.onend = () => { clear(); resolve(); };
        current.onerror = e => { clear(); reject(Object.assign(new Error("The browser voice stopped; press Listen to try again"), { name: e.error === "not-allowed" ? "NotAllowedError" : "SpeechError" })); };
        signal.addEventListener("abort", cancelled, { once: true });
        if (signal.aborted) cancelled(); else speechSynthesis.speak(current);
      });
    }
  })();
  signal.addEventListener("abort", abort, { once: true });
  return { done: done.finally(() => { signal.removeEventListener("abort", abort); current = null; }), pause: () => { paused = true; speechSynthesis.pause(); }, resume: () => { paused = false; wake?.(); speechSynthesis.resume(); }, rate: n => { rate = n; if (current) current.rate = n; } };
}
